// Auth API — talks to lendcord-core directly (see webpack.config.js's
// devServer.proxy for /auth, matching lendcord-core's Postman collection:
// bare paths, no /api prefix).

import { baseApi } from "@/app/store/baseApi";

export interface AuthTokenResponse {
  accessToken: string;
  tokenType: string;
  expiresInSeconds: number;
  // Set true when an agent/admin onboarded this user with a generated
  // password (see lendcord-core's User.mustChangePassword) — the caller
  // must rotate it via /auth/password/change before using the app further.
  mustChangePassword: boolean;
  isVerified: boolean;
}

export interface SignupParams {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  role: string;
  consentGiven: boolean;
}

export type LoginIdentifierType = "EMAIL" | "PHONE";

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Step 1 of 2. Creates no account yet — the applicant's data is staged
    // server-side and OTPs are sent to both email and phone as a side
    // effect. The returned sessionToken is required by verifySignup below.
    signup: builder.mutation<{ sessionToken: string }, SignupParams>({
      query: (params) => ({ url: "/auth/signup", method: "POST", body: params }),
    }),

    // Step 2 of 2 — the only place an account actually gets created, once
    // both OTPs verify. Returns the same token shape as login.
    verifySignup: builder.mutation<
      AuthTokenResponse,
      { sessionToken: string; emailOtp: string; phoneOtp: string }
    >({
      query: ({ sessionToken, emailOtp, phoneOtp }) => ({
        url: "/auth/verify",
        method: "POST",
        body: {
          sessionToken,
          otps: [
            { type: "EMAIL", otp: emailOtp },
            { type: "PHONE", otp: phoneOtp },
          ],
        },
      }),
    }),

    login: builder.mutation<
      AuthTokenResponse,
      { identifierType: LoginIdentifierType; identifier: string; password: string }
    >({
      query: (body) => ({ url: "/auth/login/password", method: "POST", body }),
    }),

    // Revokes the token used to authenticate this call (one device/session,
    // not "log out everywhere"). The caller clears its locally stored token
    // regardless of whether this succeeds — see TopBar's handleLogout.
    logout: builder.mutation<void, void>({
      query: () => ({ url: "/auth/logout", method: "POST", auth: true }),
    }),

    // Step 1 of the forgot-password flow. Always resolves with no body — the
    // backend returns 204 whether or not the identifier matches an account
    // (account-enumeration prevention), so the UI can't and shouldn't try to
    // distinguish those cases. Single-channel: a code is sent only to
    // whichever identifier was used to look the account up, not both.
    requestPasswordReset: builder.mutation<void, { identifierType: LoginIdentifierType; identifier: string }>({
      query: (body) => ({ url: "/auth/password/reset/request", method: "POST", body }),
    }),

    // Step 2 — the single OTP from step 1 plus the new password. Returns the
    // same token shape as login/signup, so a successful reset logs the user
    // straight in without a separate login call.
    confirmPasswordReset: builder.mutation<
      AuthTokenResponse,
      { identifierType: LoginIdentifierType; identifier: string; otp: string; newPassword: string }
    >({
      query: (body) => ({ url: "/auth/password/reset/confirm", method: "POST", body }),
    }),

    // The authenticated counterpart to password reset — proof of identity is
    // the old password itself, not an OTP, since the caller already holds a
    // session. A wrong old password is a legitimate 401 (LEND-072), not a
    // bad-token situation, so this opts out of apiClient's default
    // auto-logout-on-401.
    changePassword: builder.mutation<void, { oldPassword: string; newPassword: string }>({
      query: (body) => ({
        url: "/auth/password/change",
        method: "POST",
        body,
        auth: true,
        skipAuthExpiryOn401: true,
      }),
    }),
  }),
});

export const {
  useSignupMutation,
  useVerifySignupMutation,
  useLoginMutation,
  useLogoutMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useChangePasswordMutation,
} = authApi;
